/**
 * Expired Package Trigger Component
 *
 * Trigger: "a package has expired"
 * Shows: Package select dropdown for filtering by specific package
 *
 * V2 Reference: v2/app/components/client-reminder-cancel.js
 * V2 Template: v2/app/templates/components/client-reminder-cancel.hbs
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Skeleton } from 'src/components/Skeleton';
import { TextField } from 'src/components/TextField';
import { fetchPackageOptions, packageListQueryKey } from 'src/models/package/package';

import { TriggerComponentProps } from '../automationTriggers';

export const ExpiredPackageTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const { data: packages = [], isLoading } = useQuery({
    queryKey: packageListQueryKey,
    queryFn: () => fetchPackageOptions(),
    staleTime: 60000,
  });

  if (readOnly) {
    const selectedPackage = packages.find(
      (p) => p.id?.toString() === automation.reminderTypeId?.toString()
    );
    if (selectedPackage) {
      return (
        <span>
          {t('automations.triggers.expiredPackageFor', {
            package: selectedPackage.name,
          })}
        </span>
      );
    }
    return <span>{t('automations.triggers.expiredPackageAny')}</span>;
  }

  if (isLoading) {
    return <Skeleton variant="text" width={200} height={40} />;
  }

  const packageOptions = [
    { value: '', label: t('automations.triggers.selectPackage') },
    ...packages.map((p) => ({
      value: p.id?.toString() || '',
      label: p.name,
    })),
  ];

  return (
    <Box sx={{ minWidth: 200 }}>
      <TextField
        value={automation.reminderTypeId?.toString() || ''}
        options={packageOptions}
        onChange={(e) => {
          const value = e.target.value as string;
          setFieldValue(
            `automations.${index}.reminderTypeId`,
            value || null
          );
        }}
        size="small"
      />
    </Box>
  );
};
